import React, { useRef } from 'react';
import styled from 'styled-components';
import { motion, useScroll, useTransform } from 'framer-motion';

const marqueeItems = [
  'Microneedling', '✦', 'Hydrodermabrasion', '✦', 'RF Lifting', '✦', 'LED Therapy', '✦',
  'Chemical Peels', '✦', 'Skin Boosters', '✦', 'Dermaplaning', '✦', 'Oxygen Facial', '✦',
];

const protocols = [
  { step: '01', title: 'Consultation', desc: 'A detailed skin analysis session to understand your skin type, history, goals, and any sensitivities. We map your perfect protocol.' },
  { step: '02', title: 'Preparation', desc: 'Gentle cleanse and prep to prime the skin — ensuring every treatment penetrates optimally and delivers its full potential.' },
  { step: '03', title: 'Treatment', desc: 'Your personalized protocol is expertly delivered using our clinical-grade devices — comfortable, precise, and results-driven.' },
  { step: '04', title: 'Recovery & Glow', desc: 'Post-treatment care, custom aftercare guidance, and a recovery plan designed around your lifestyle and skin needs.' },
];

const Treatments = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start end', 'end start'] });
  const marqueeX = useTransform(scrollYProgress, [0, 1], ['0%', '-30%']);

  return (
    <Section id="treatments" ref={sectionRef}>
      {/* Marquee */}
      <MarqueeWrap>
        <MarqueeLine style={{ x: marqueeX }}>
          {[...marqueeItems, ...marqueeItems, ...marqueeItems].map((item, i) => (
            <MarqueeItem key={i} $isDot={item === '✦'}>{item}</MarqueeItem>
          ))}
        </MarqueeLine>
      </MarqueeWrap>

      <Inner>
        <HeadRow>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
          >
            <Label>The Elaia Experience</Label>
            <Title>Your Treatment<br /><em>Protocol</em></Title>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2 }}
          >
            <HeadDesc>
              Every Elaia treatment follows a thoughtfully crafted journey — from first consultation to radiant results. No shortcuts. No guesswork.
            </HeadDesc>
          </motion.div>
        </HeadRow>

        <ProtocolGrid>
          {protocols.map((p, i) => (
            <ProtocolCard
              key={p.step}
              as={motion.div}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
            >
              <StepNum>{p.step}</StepNum>
              <ConnectorLine $last={i === protocols.length - 1} />
              <StepTitle>{p.title}</StepTitle>
              <StepDesc>{p.desc}</StepDesc>
            </ProtocolCard>
          ))}
        </ProtocolGrid>

        <BottomCta>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <CtaBox>
              <CtaLabel>Ready to begin?</CtaLabel>
              <CtaTitle>Your skin's best chapter starts here.</CtaTitle>
              <CtaBtn
                as={motion.a}
                href="#contact"
                whileHover={{ scale: 1.02, backgroundColor: 'var(--gold)' }}
                whileTap={{ scale: 0.98 }}
              >
                Book a Consultation
              </CtaBtn>
            </CtaBox>
          </motion.div>
        </BottomCta>
      </Inner>
    </Section>
  );
};

const Section = styled.section`
  background: var(--charcoal-soft);
  padding: 0 0 140px;
  overflow: hidden;
`;
const MarqueeWrap = styled.div`
  overflow: hidden;
  border-top: 1px solid rgba(196, 149, 122, 0.15);
  border-bottom: 1px solid rgba(196, 149, 122, 0.15);
  padding: 22px 0;
  margin-bottom: 100px;
  background: rgba(196, 149, 122, 0.03);
`;
const MarqueeLine = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0;
  white-space: nowrap;
  width: max-content;
`;
const MarqueeItem = styled.span`
  font-family: ${({ $isDot }) => $isDot ? 'sans-serif' : 'var(--font-display)'};
  font-size: ${({ $isDot }) => $isDot ? '14px' : '18px'};
  font-style: ${({ $isDot }) => $isDot ? 'normal' : 'italic'};
  font-weight: 300;
  color: ${({ $isDot }) => $isDot ? 'var(--rose)' : 'var(--taupe)'};
  letter-spacing: 0.05em;
  padding: 0 28px;
  opacity: ${({ $isDot }) => $isDot ? '0.5' : '1'};
`;
const Inner = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 60px;
  @media (max-width: 768px) { padding: 0 24px; }
`;
const HeadRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 60px;
  margin-bottom: 80px;
  flex-wrap: wrap;
`;
const Label = styled.span`
  display: block;
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--rose);
  margin-bottom: 20px;
`;
const Title = styled.h2`
  font-family: var(--font-display);
  font-size: clamp(44px, 5.5vw, 80px);
  font-weight: 300;
  color: var(--cream);
  letter-spacing: -0.02em;
  line-height: 1.05;
  margin: 0;
  em { font-style: italic; color: var(--rose); }
`;
const HeadDesc = styled.p`
  max-width: 380px;
  font-family: var(--font-body);
  font-size: 15px;
  color: var(--taupe);
  line-height: 1.8;
  font-weight: 300;
`;
const ProtocolGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  border: 1px solid rgba(196, 149, 122, 0.1);
  margin-bottom: 80px;

  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 500px) { grid-template-columns: 1fr; }
`;
const ProtocolCard = styled.div`
  padding: 52px 40px;
  border-right: 1px solid rgba(196, 149, 122, 0.1);
  position: relative;
  &:last-child { border-right: none; }
  &:hover { background: rgba(196, 149, 122, 0.03); }
  transition: background 0.3s;

  @media (max-width: 768px) { padding: 36px 28px; }
`;
const StepNum = styled.div`
  font-family: var(--font-display);
  font-size: 11px;
  color: rgba(196, 149, 122, 0.4);
  letter-spacing: 0.1em;
  margin-bottom: 24px;
`;
const ConnectorLine = styled.div`
  display: none;
`;
const StepTitle = styled.h4`
  font-family: var(--font-display);
  font-size: clamp(22px, 2.2vw, 30px);
  font-weight: 400;
  color: var(--cream);
  margin: 0 0 16px;
`;
const StepDesc = styled.p`
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 300;
  color: var(--taupe);
  line-height: 1.8;
  margin: 0;
`;
const BottomCta = styled.div``;
const CtaBox = styled.div`
  border: 1px solid rgba(196, 149, 122, 0.2);
  background: rgba(196, 149, 122, 0.04);
  padding: 72px 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 28px;

  @media (max-width: 768px) { padding: 52px 28px; }
`;
const CtaLabel = styled.span`
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--rose);
`;
const CtaTitle = styled.h3`
  font-family: var(--font-display);
  font-size: clamp(28px, 4vw, 52px);
  font-weight: 300;
  font-style: italic;
  color: var(--cream);
  margin: 0;
  letter-spacing: -0.01em;
`;
const CtaBtn = styled.a`
  display: inline-block;
  text-decoration: none;
  color: var(--charcoal);
  background: var(--rose);
  font-family: var(--font-body);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  padding: 18px 52px;
  transition: background 0.3s;
`;

export default Treatments;
